import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import CollaborationPanel from './CollaborationPanel';
import type { CollaborationScope, CollaborationTarget } from './collaboration-types';
import { buildStudyTarget } from './collaboration-types';

type CollaborationContextValue = {
  isOpen: boolean;
  target: CollaborationTarget | null;
  /** Current viewer frame/finding context — updated as the user navigates. */
  viewerLocation: CollaborationTarget | null;
  scope: CollaborationScope;
  initialDiscussionId: string | null;
  /** Bumped on every openCollaboration call so the panel resets when switching location. */
  locationSeq: number;
  openCollaboration: (
    target?: CollaborationTarget | null,
    discussionId?: string | null
  ) => void;
  closeCollaboration: () => void;
  setScope: (scope: CollaborationScope) => void;
  setViewerLocation: (location: CollaborationTarget | null) => void;
  clearInitialDiscussionId: () => void;
};

const CollaborationContext = createContext<CollaborationContextValue | null>(null);

export function CollaborationProvider({
  studyInstanceUid,
  children,
}: {
  studyInstanceUid: string;
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [target, setTarget] = useState<CollaborationTarget | null>(null);
  const [viewerLocation, setViewerLocation] = useState<CollaborationTarget | null>(null);
  const [scope, setScope] = useState<CollaborationScope>('location');
  const [initialDiscussionId, setInitialDiscussionId] = useState<string | null>(null);
  const [locationSeq, setLocationSeq] = useState(0);

  const openCollaboration = useCallback(
    (nextTarget?: CollaborationTarget | null, discussionId?: string | null) => {
      if (nextTarget === undefined) {
        setTarget(buildStudyTarget(studyInstanceUid));
        setScope('all');
      } else if (nextTarget === null) {
        setTarget(null);
        setScope('all');
      } else if (nextTarget.type === 'STUDY') {
        setTarget(nextTarget);
        setScope('all');
      } else {
        setTarget(nextTarget);
        setViewerLocation(nextTarget);
        setScope('location');
      }
      setInitialDiscussionId(discussionId ?? null);
      setLocationSeq((seq) => seq + 1);
      setIsOpen(true);
    },
    [studyInstanceUid]
  );

  const closeCollaboration = useCallback(() => {
    setIsOpen(false);
  }, []);

  const clearInitialDiscussionId = useCallback(() => {
    setInitialDiscussionId(null);
  }, []);

  const value = useMemo(
    () => ({
      isOpen,
      target,
      viewerLocation,
      scope,
      initialDiscussionId,
      locationSeq,
      openCollaboration,
      closeCollaboration,
      setScope,
      setViewerLocation,
      clearInitialDiscussionId,
    }),
    [
      isOpen,
      target,
      viewerLocation,
      scope,
      initialDiscussionId,
      locationSeq,
      openCollaboration,
      closeCollaboration,
      clearInitialDiscussionId,
    ]
  );

  return (
    <CollaborationContext.Provider value={value}>
      {children}
      <CollaborationPanel studyInstanceUid={studyInstanceUid} />
    </CollaborationContext.Provider>
  );
}

export function useCollaboration(): CollaborationContextValue {
  const ctx = useContext(CollaborationContext);
  if (!ctx) {
    throw new Error('useCollaboration must be used within CollaborationProvider');
  }
  return ctx;
}

export function useCollaborationOptional(): CollaborationContextValue | null {
  return useContext(CollaborationContext);
}

/** Register the viewer's current frame/finding as the collaboration "this location" context. */
export function useCollaborationViewerLocation(
  location: CollaborationTarget | null,
  enabled = true
) {
  const ctx = useCollaborationOptional();
  const locationKey = location ? `${location.type}:${location.ref}` : null;

  useEffect(() => {
    if (!ctx || !enabled) return;
    ctx.setViewerLocation(location);
  }, [ctx, enabled, locationKey, location?.label]);

  useEffect(() => {
    if (!ctx || !enabled) return;
    return () => ctx.setViewerLocation(null);
  }, [ctx, enabled]);
}
